import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ladingAPI, salesAPI, finishedGoodAPI, warehouseAPI } from '../utils/api';
import { FileText, Plus, Edit2, Trash2, X, ChevronDown, ChevronUp, Search, CheckSquare, ShoppingCart, Link2, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import { Thumb, ImagePicker } from '../components/common/ImageZoom';
import { localName, fmt, money, apiError, today } from '../utils/helpers';
import SectionHero from '../components/common/SectionHero';

const emptyItem = () => ({ finished_good_id: '', warehouse_id: '', original_quantity_rolls: '', original_quantity_sqm: '', customs_quantity_rolls: '', customs_quantity_sqm: '', customs_fee: '' });
const emptyForm = () => ({ bill_date: today(), customer_id: '', destination: '', original_bill_number: '', has_customs: false, customs_bill_number: '', notes: '', sale_id: null, items: [emptyItem()] });
const num = v => (v === '' || v == null ? null : parseFloat(v));

export default function LadingBills() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuthStore();
  const canEdit = ['admin', 'manager', 'sales_manager'].includes(user?.role);
  const isAdmin = user?.role === 'admin';

  const [bills, setBills] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [goods, setGoods] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [customsOnly, setCustomsOnly] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editBill, setEditBill] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [image, setImage] = useState(null);
  const [saleLabel, setSaleLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    return Promise.all([ladingAPI.list(), salesAPI.listCustomers(), finishedGoodAPI.list(), warehouseAPI.list()])
      .then(([b, c, g, w]) => { setBills(b.data); setCustomers(c.data); setGoods(g.data); setWarehouses(w.data); })
      .catch(() => toast.error(t('error')))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Issue a bill for an existing sale: /lading-bills?from_sale=<id>
  useEffect(() => {
    const saleId = params.get('from_sale');
    if (!saleId) return;
    salesAPI.get(saleId).then(r => {
      const s = r.data;
      if (s.lading_bill_id) { toast.error(t('sale_already_linked')); return; }
      setEditBill(null); setImage(null); setSaleLabel(s.sale_number);
      setForm({ ...emptyForm(), bill_date: s.sale_date, customer_id: s.customer_id || '', has_customs: s.has_customs, sale_id: s.id,
        items: s.items.map(i => ({ ...emptyItem(), finished_good_id: i.finished_good_id, warehouse_id: i.warehouse_id || '',
          original_quantity_rolls: i.quantity_rolls, original_quantity_sqm: i.quantity_sqm })) });
      setShowModal(true);
    }).catch(() => toast.error(t('error')));
    params.delete('from_sale'); setParams(params, { replace: true });
  }, [params]); // eslint-disable-line

  const q = search.trim().toLowerCase();
  const filtered = bills.filter(b =>
    (!customsOnly || b.has_customs) &&
    (!q || [b.original_bill_number, b.customs_bill_number, b.customer, b.destination, b.sale_number].some(v => (v || '').toLowerCase().includes(q)))
  );

  const toggleExpand = id => setExpanded(e => ({ ...e, [id]: !e[id] }));
  const updateItem = (i, field, val) => setForm(f => { const items = [...f.items]; items[i] = { ...items[i], [field]: val }; return { ...f, items }; });
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = i => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const stockOf = (gid, wid) => goods.find(g => g.id === gid)?.stocks.find(s => s.warehouse_id === wid)?.quantity_rolls;

  const openCreate = () => { setEditBill(null); setForm(emptyForm()); setImage(null); setSaleLabel(''); setShowModal(true); };
  const openEdit = b => {
    setEditBill(b); setSaleLabel('');
    setForm({ ...emptyForm(), bill_date: b.bill_date, customer_id: b.customer_id || '', destination: b.destination || '', original_bill_number: b.original_bill_number || '',
      has_customs: b.has_customs, customs_bill_number: b.customs_bill_number || '', notes: b.notes || '', items: [] });
    setImage(b.image_path || null);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!editBill && form.items.some(i => !i.finished_good_id || !i.warehouse_id || !i.original_quantity_rolls)) return toast.error(t('fill_all_items'));
    setSaving(true);
    try {
      let id = editBill?.id;
      if (editBill) {
        await ladingAPI.update(id, { bill_date: form.bill_date, customer_id: form.customer_id || '', original_bill_number: form.original_bill_number,
          has_customs: form.has_customs, customs_bill_number: form.customs_bill_number, destination: form.destination, notes: form.notes });
      } else {
        id = (await ladingAPI.create({ ...form, customer_id: form.customer_id || null,
          items: form.items.map(i => ({ finished_good_id: i.finished_good_id, warehouse_id: i.warehouse_id,
            original_quantity_rolls: parseFloat(i.original_quantity_rolls), original_quantity_sqm: parseFloat(i.original_quantity_sqm) || 0,
            customs_quantity_rolls: form.has_customs ? num(i.customs_quantity_rolls) : null, customs_quantity_sqm: form.has_customs ? num(i.customs_quantity_sqm) : null,
            customs_fee: form.has_customs ? parseFloat(i.customs_fee) || 0 : 0 })) })).data.id;
      }
      if (image instanceof File) await ladingAPI.uploadImage(id, image);
      toast.success(t('saved')); setShowModal(false); load();
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm(t('confirm_delete'))) return;
    try { await ladingAPI.delete(id); toast.success(t('deleted')); load(); } catch (e) { toast.error(apiError(e, t)); }
  };

  const cols = canEdit ? 8 : 7;

  return (
    <div className="space-y-5">
      <SectionHero title={t('lading_bills')} subtitle={<>{bills.length} {t('bills')} · {bills.filter(b => !b.sale_id).length} {t('not_invoiced')}</>}>
        {canEdit && <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> {t('new_bill')}</button>}
      </SectionHero>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 200px' }}>
            <Search size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input className="input" style={{ border: 'none', background: 'transparent', padding: 0 }} placeholder={t('search_bills')} value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="checkbox-wrap" onClick={() => setCustomsOnly(v => !v)}>
            <input type="checkbox" checked={customsOnly} readOnly /><span className="text-sm">{t('customs_only')}</span>
          </div>
        </div>

        {loading ? <div className="loading-center"><div className="spinner" /></div>
          : filtered.length === 0 ? <div className="empty-state"><FileText size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} /><p>{t('no_data')}</p></div>
          : (
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th style={{ width: 32 }}></th>
                  <th>{t('date')}</th>
                  <th>{t('bill_number')}</th>
                  <th>{t('customer')}</th>
                  <th className="hide-mobile">{t('destination')}</th>
                  <th>{t('customs')}</th>
                  <th>{t('sale')}</th>
                  {canEdit && <th>{t('actions')}</th>}
                </tr></thead>
                <tbody>
                  {filtered.map(b => (
                    <React.Fragment key={b.id}>
                      <tr style={{ cursor: 'pointer' }} onClick={() => toggleExpand(b.id)}>
                        <td>{expanded[b.id] ? <ChevronUp size={14} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />}</td>
                        <td>{b.bill_date}</td>
                        <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{b.original_bill_number || '—'}{b.image_path && <ImageIcon size={11} style={{ marginInlineStart: 6, color: 'var(--text-muted)' }} />}</td>
                        <td>{b.customer || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                        <td className="hide-mobile">{b.destination || '—'}</td>
                        <td>{b.has_customs ? <span className="badge badge-orange"><CheckSquare size={10} /> {b.customs_bill_number || t('yes')}</span> : <span className="badge" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-muted)' }}>{t('no')}</span>}</td>
                        <td>{b.sale_id ? <span className="badge badge-green"><Link2 size={10} /> {b.sale_number}</span> : <span className="badge badge-yellow">{t('not_invoiced')}</span>}</td>
                        {canEdit && (
                          <td onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-1">
                              {!b.sale_id && (
                                <button className="btn btn-sm" style={{ background: 'var(--green-dim)', color: '#34d399', border: '1px solid rgba(16,185,129,0.25)' }} onClick={() => navigate(`/sales?from_bill=${b.id}`)}>
                                  <ShoppingCart size={13} /> {t('convert_to_sale')}
                                </button>
                              )}
                              <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEdit(b)}><Edit2 size={13} /></button>
                              {isAdmin && <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(b.id)}><Trash2 size={13} /></button>}
                            </div>
                          </td>
                        )}
                      </tr>
                      {expanded[b.id] && (
                        <tr><td colSpan={cols} style={{ background: 'rgba(0,0,0,0.2)', padding: '12px 20px' }}>
                          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            {b.image_path && <Thumb src={b.image_path} size={96} />}
                            <div style={{ flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {b.items.map((it, i) => (
                                <div key={i} className="flex items-center justify-between text-xs" style={{ background: 'var(--bg-card)', padding: '6px 10px', borderRadius: 8, gap: 8, flexWrap: 'wrap' }}>
                                  <span style={{ color: 'var(--text-primary)' }}>{localName(it, lang)}</span>
                                  <span style={{ color: 'var(--text-secondary)' }}>
                                    {t('original')}: {fmt(it.original_quantity_rolls)} {t('rolls')} ({fmt(it.original_quantity_sqm)} {t('sqm')})
                                    {it.customs_quantity_rolls != null ? ` · ${t('customs')}: ${fmt(it.customs_quantity_rolls)} ${t('rolls')}` : ''}
                                    {it.customs_fee ? ` · ${t('customs_fee')}: ${money(it.customs_fee)}` : ''}
                                  </span>
                                </div>
                              ))}
                              {b.notes && <p className="text-xs mt-1" style={{ color: 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>{b.notes}</p>}
                            </div>
                          </div>
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="card-title">{editBill ? t('edit_bill') : t('new_bill')}</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {form.sale_id && <div className="alert alert-success mb-3"><Link2 size={15} /> <span>{t('bill_for_sale')}: <strong>{saleLabel}</strong> — {t('sale_stock_note')}</span></div>}
              <div className="form-row">
                <div className="form-group"><label className="form-label">{t('date')}</label><input className="input" type="date" value={form.bill_date} onChange={e => setForm(f => ({ ...f, bill_date: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">{t('customer')}</label>
                  <select className="select" value={form.customer_id} onChange={e => setForm(f => ({ ...f, customer_id: e.target.value }))}>
                    <option value="">{t('no_customer')}</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">{t('bill_number')}</label><input className="input" value={form.original_bill_number} onChange={e => setForm(f => ({ ...f, original_bill_number: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">{t('destination')}</label><input className="input" value={form.destination} onChange={e => setForm(f => ({ ...f, destination: e.target.value }))} /></div>
              </div>
              <div className="checkbox-wrap mb-3" onClick={() => setForm(f => ({ ...f, has_customs: !f.has_customs }))}>
                <input type="checkbox" checked={form.has_customs} readOnly />
                <label style={{ cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)' }}>{t('has_customs_version')}</label>
              </div>
              {form.has_customs && <div className="form-group"><label className="form-label">{t('customs_bill_number')}</label><input className="input" value={form.customs_bill_number} onChange={e => setForm(f => ({ ...f, customs_bill_number: e.target.value }))} /></div>}

              {!editBill && (
                <>
                  <div className="divider" />
                  <div className="flex items-center justify-between mb-3">
                    <p className="form-label" style={{ margin: 0 }}>{t('items')}</p>
                    {!form.sale_id && <button className="btn btn-secondary btn-sm" onClick={addItem}><Plus size={13} /> {t('add')}</button>}
                  </div>
                  {form.items.map((item, i) => {
                    const avail = stockOf(item.finished_good_id, item.warehouse_id);
                    return (
                      <div key={i} style={{ padding: 12, borderRadius: 12, background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', marginBottom: 8 }}>
                        <div className="flex gap-2 flex-wrap mb-2 items-center">
                          <select className="select" disabled={!!form.sale_id} value={item.finished_good_id} onChange={e => updateItem(i, 'finished_good_id', e.target.value)} style={{ flex: '2 1 150px' }}>
                            <option value="">{t('select_product')}</option>
                            {goods.map(g => <option key={g.id} value={g.id}>{localName(g, lang)}</option>)}
                          </select>
                          <select className="select" value={item.warehouse_id} onChange={e => updateItem(i, 'warehouse_id', e.target.value)} style={{ flex: '2 1 130px' }}>
                            <option value="">{t('select_warehouse')}</option>
                            {warehouses.map(w => { const st = stockOf(item.finished_good_id, w.id); return <option key={w.id} value={w.id}>{w.name}{item.finished_good_id && st != null ? ` (${fmt(st)})` : ''}</option>; })}
                          </select>
                          {form.items.length > 1 && !form.sale_id && <button className="btn btn-ghost btn-icon" onClick={() => removeItem(i)}><X size={14} /></button>}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <input className="input" type="number" min="0" step="any" placeholder={t('original') + ' ' + t('rolls') + ' *'} value={item.original_quantity_rolls} onChange={e => updateItem(i, 'original_quantity_rolls', e.target.value)} style={{ flex: '1 1 100px' }} />
                          <input className="input" type="number" min="0" step="any" placeholder={t('original') + ' ' + t('sqm')} value={item.original_quantity_sqm} onChange={e => updateItem(i, 'original_quantity_sqm', e.target.value)} style={{ flex: '1 1 90px' }} />
                          {form.has_customs && <>
                            <input className="input" type="number" min="0" step="any" placeholder={t('customs') + ' ' + t('rolls')} value={item.customs_quantity_rolls} onChange={e => updateItem(i, 'customs_quantity_rolls', e.target.value)} style={{ flex: '1 1 90px' }} />
                            <input className="input" type="number" min="0" step="any" placeholder={t('customs') + ' ' + t('sqm')} value={item.customs_quantity_sqm} onChange={e => updateItem(i, 'customs_quantity_sqm', e.target.value)} style={{ flex: '1 1 90px' }} />
                            <input className="input" type="number" min="0" step="any" placeholder={t('customs_fee')} value={item.customs_fee} onChange={e => updateItem(i, 'customs_fee', e.target.value)} style={{ flex: '1 1 90px' }} />
                          </>}
                        </div>
                        {!form.sale_id && item.finished_good_id && item.warehouse_id && <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{t('available')}: {fmt(avail || 0)} {t('rolls')}</p>}
                      </div>
                    );
                  })}
                </>
              )}

              <div className="divider" />
              <ImagePicker label={t('bill_image')} value={image} onChange={setImage} />
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('notes')}</label>
                <textarea className="input" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ minHeight: 56 }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>{t('cancel')}</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('loading') : t('save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
